import {
  type ProjectFolderType,
  type ProjectFolderWithProjectsType,
  type ProjectType,
} from '@/types/types';

import { rtkQueryApi } from './rtk-query-api';

export const projectFoldersApi = rtkQueryApi
  .enhanceEndpoints({ addTagTypes: ['ProjectFolders', 'ProjectsInFolder'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Получить все папки (без проектов внутри)
      getProjectFolders: builder.query<ProjectFolderType[], void>({
        query: () => '/user-project-lists',
        transformResponse: (res: { data: ProjectFolderType[] }): ProjectFolderType[] => res.data,
        providesTags: ['ProjectFolders'],
      }),

      // Получить проекты в конкретной папке
      getProjectsInFolder: builder.query<ProjectType[], number>({
        query: (folderId) => `/user-project-lists/${folderId}`,
        transformResponse: (res: { data: { projects: ProjectType[] } }): ProjectType[] =>
          res.data.projects,
        providesTags: ['ProjectsInFolder'],
      }),

      // Создать новую папку
      createProjectFolder: builder.mutation<ProjectFolderWithProjectsType, { name: string }>({
        query: ({ name }) => ({
          url: '/user-project-lists',
          method: 'POST',
          body: { name },
        }),
        transformResponse: (res: {
          data: {
            projectList: Omit<ProjectFolderWithProjectsType, 'projects'>;
            projects: ProjectType[];
          };
        }) => ({
          ...res.data.projectList,
          projects: res.data.projects,
        }),
      }),

      // Удалить папку
      deleteProjectFolder: builder.mutation<void, number>({
        query: (id) => ({
          url: `/user-project-lists/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['ProjectFolders'],
      }),

      // Добавить проекты в папку
      addProjectsToFolder: builder.mutation<
        ProjectFolderWithProjectsType,
        { folderId: number; projectIds: number[] }
      >({
        query: ({ folderId, projectIds }) => ({
          url: `/user-project-lists/${folderId}/add`,
          method: 'POST',
          body: { projects: projectIds },
        }),
        transformResponse: (res: {
          data: {
            projectList: Omit<ProjectFolderWithProjectsType, 'projects'>;
            projects: ProjectType[];
          };
        }): ProjectFolderWithProjectsType => ({
          ...res.data.projectList,
          projects: res.data.projects,
        }),
        invalidatesTags: ['ProjectsInFolder'],
      }),

      // Удалить проекты из папки
      removeProjectsFromFolder: builder.mutation<void, { folderId: number; projectIds: number[] }>({
        query: ({ folderId, projectIds }) => ({
          url: `/user-project-lists/${folderId}/remove`,
          method: 'POST',
          body: { projects: projectIds },
        }),
      }),

      // Переместить проекты в другую папку
      moveProjectsToFolder: builder.mutation<
        ProjectFolderWithProjectsType,
        { folderId: number; projectIds: number[]; toListId: number }
      >({
        query: ({ folderId, projectIds, toListId  }) => ({
          url: `/user-project-lists/${folderId}/move`,
          method: 'POST',
          body: { toListId, projects: projectIds },
        }),
        transformResponse: (res: {
          data: {
            userProjectList: Omit<ProjectFolderWithProjectsType, 'projects'>;
            projects: ProjectType[];
          };
        }): ProjectFolderWithProjectsType => ({
          ...res.data.userProjectList,
          projects: res.data.projects,
        }),
      }),

      // Дублировать проекты в папку
      duplicateProjectsToFolder: builder.mutation<
        ProjectFolderWithProjectsType,
        { folderId: number; projectIds: number[]; toListId: number }
      >({
        query: ({ folderId, projectIds, toListId }) => ({
          url: `/user-project-lists/${folderId}/duplicate`,
          method: 'POST',
          body: { toListId, projects: projectIds },
        }),
        transformResponse: (res: {
          data: {
            userProjectList: Omit<ProjectFolderWithProjectsType, 'projects'>;
            projects: ProjectType[];
          };
        }): ProjectFolderWithProjectsType => ({
          ...res.data.userProjectList,
          projects: res.data.projects,
        }),
      }),

      // Переименовать папку
      renameProjectFolder: builder.mutation<
        ProjectFolderWithProjectsType,
        { id: number; name: string }
      >({
        query: ({ id, name }) => ({
          url: `/user-project-lists/rename/${id}`,
          method: 'POST',
          body: { name },
        }),
        transformResponse: (res: {
          data: ProjectFolderWithProjectsType;
        }): ProjectFolderWithProjectsType => res.data,
        invalidatesTags: ['ProjectFolders'],
      }),
    }),
  });

export const {
  useLazyGetProjectFoldersQuery,
  useLazyGetProjectsInFolderQuery,
  useCreateProjectFolderMutation,
  useDeleteProjectFolderMutation,
  useAddProjectsToFolderMutation,
  useRemoveProjectsFromFolderMutation,
  useMoveProjectsToFolderMutation,
  useDuplicateProjectsToFolderMutation,
  useRenameProjectFolderMutation,
} = projectFoldersApi;